import Phaser from 'phaser';
import { TILE_SIZE, MAP_WIDTH, MAP_HEIGHT, Direction } from '../data/world-config';
import { PLANTS } from '../data/plants';
import { MONSTERS } from '../data/monsters';
import type { GameScene } from '../scenes/GameScene';

interface RivalFarmPlot {
  x: number;
  y: number;
  plantId?: string;
  growthStage: number;
}

export class AIRivalSystem {
  private scene: GameScene;

  // Rival sprite and state
  rivalSprite!: Phaser.Physics.Arcade.Sprite;
  private rivalDirection: Direction = Direction.DOWN;
  private rivalFarmOriginX: number;
  private rivalFarmOriginY: number;
  private rivalPlots: Map<string, RivalFarmPlot> = new Map();
  private rivalPlantSprites: Map<string, Phaser.GameObjects.Sprite> = new Map();

  // AI behavior
  private aiTimer: number = 0;
  private aiActionInterval: number = 3000; // ms between AI decisions
  private rivalPlants: string[] = ['moonbloom', 'shadow_vine', 'fire_pepper'];
  private rivalName: string = 'Thornwick';

  // Battle challenge state
  challengeReady: boolean = false;
  private challengeDay: number = 7;

  // Crop raid system — Thornwick sends scouts to destroy your plants on Days 5-6
  private raidTimer: number = 0;
  private raidInterval: number = 30_000;
  private hasRaidedToday: boolean = false;

  constructor(scene: GameScene) {
    this.scene = scene;

    // Place rival's farm in the northeast
    this.rivalFarmOriginX = Math.floor(MAP_WIDTH * 0.75);
    this.rivalFarmOriginY = Math.floor(MAP_HEIGHT * 0.25);

    this.createRivalFarm();
    this.createRivalSprite();

    // Reset raid flag each new day
    this.scene.events.on('new-day', () => {
      this.hasRaidedToday = false;
    });
  }

  private createRivalFarm(): void {
    const plotSize = 6;
    const offsetX = this.rivalFarmOriginX;
    const offsetY = this.rivalFarmOriginY;

    // Create fences
    for (let i = -1; i <= plotSize; i++) {
      for (const edge of [-1, plotSize]) {
        // Horizontal fences
        this.scene.add.sprite(
          (offsetX + i) * TILE_SIZE + TILE_SIZE / 2,
          (offsetY + edge) * TILE_SIZE + TILE_SIZE / 2,
          'terrain', 28
        ).setDepth(2);
        // Vertical fences
        this.scene.add.sprite(
          (offsetX + edge) * TILE_SIZE + TILE_SIZE / 2,
          (offsetY + i) * TILE_SIZE + TILE_SIZE / 2,
          'terrain', 29
        ).setDepth(2);
      }
    }

    // Create plots
    for (let y = 0; y < plotSize; y++) {
      for (let x = 0; x < plotSize; x++) {
        const tileX = offsetX + x;
        const tileY = offsetY + y;
        const key = `${tileX},${tileY}`;

        const plot: RivalFarmPlot = {
          x: tileX,
          y: tileY,
          growthStage: 0,
        };

        // Pre-plant some crops
        if ((x + y) % 3 !== 0) {
          const plantId = this.rivalPlants[Math.floor(Math.random() * this.rivalPlants.length)];
          plot.plantId = plantId;
          plot.growthStage = 2 + Math.floor(Math.random() * 2); // Growing or mature

          const species = PLANTS[plantId];
          if (species) {
            const sprite = this.scene.add.sprite(
              tileX * TILE_SIZE + TILE_SIZE / 2,
              tileY * TILE_SIZE + TILE_SIZE / 2,
              'plants',
              species.spriteRow * 4 + plot.growthStage
            );
            sprite.setDepth(5);
            this.rivalPlantSprites.set(key, sprite);
          }
        }

        // Soil
        this.scene.add.sprite(
          tileX * TILE_SIZE + TILE_SIZE / 2,
          tileY * TILE_SIZE + TILE_SIZE / 2,
          'terrain',
          plot.plantId ? 27 : 24
        ).setDepth(1);

        this.rivalPlots.set(key, plot);
      }
    }

    // Rival's sign
    const signSprite = this.scene.add.sprite(
      (offsetX + 3) * TILE_SIZE + TILE_SIZE / 2,
      (offsetY - 2) * TILE_SIZE + TILE_SIZE / 2,
      'objects', 10 // sign post
    );
    signSprite.setDepth(5);

    // Rival name above sign
    this.scene.add.text(
      (offsetX + 3) * TILE_SIZE + TILE_SIZE / 2,
      (offsetY - 3) * TILE_SIZE,
      'Thornwick\'s Farm',
      {
        fontFamily: 'monospace',
        fontSize: '6px',
        color: '#d4573b',
        stroke: '#000000',
        strokeThickness: 2,
      }
    ).setOrigin(0.5, 0.5).setDepth(5);
  }

  private createRivalSprite(): void {
    const spawnX = (this.rivalFarmOriginX + 3) * TILE_SIZE;
    const spawnY = (this.rivalFarmOriginY + 3) * TILE_SIZE;

    this.rivalSprite = this.scene.physics.add.sprite(spawnX, spawnY, 'player', 0);
    this.rivalSprite.setTint(0xdd5533); // Red-ish tint for rival
    this.rivalSprite.setDepth(10);
    this.rivalSprite.setCollideWorldBounds(true);

    // Rival name text that follows
    const nameTag = this.scene.add.text(spawnX, spawnY - 12, this.rivalName, {
      fontFamily: 'monospace',
      fontSize: '6px',
      color: '#d4573b',
      stroke: '#000000',
      strokeThickness: 2,
    });
    nameTag.setOrigin(0.5, 1);
    nameTag.setDepth(11);

    // Make name follow rival
    this.scene.events.on('update', () => {
      nameTag.setPosition(this.rivalSprite.x, this.rivalSprite.y - 12);
    });
  }

  update(time: number, delta: number): void {
    this.aiTimer += delta;

    if (this.aiTimer >= this.aiActionInterval) {
      this.aiTimer = 0;
      this.performAIAction(time);
    }

    // Check if player is near rival for interaction
    this.checkPlayerProximity();

    // Grow rival's plants occasionally
    if (Math.random() < 0.002) {
      this.growRandomRivalPlant();
    }

    // Crop raid system: on Days 5-6, Thornwick sends scouts during the day
    const dayCount = this.scene.dayNightSystem.getDayCount();
    const isNight = this.scene.dayNightSystem.isNight();
    if ((dayCount === 5 || dayCount === 6) && !isNight && !this.hasRaidedToday) {
      this.raidTimer += delta;
      if (this.raidTimer >= this.raidInterval) {
        this.raidTimer = 0;
        if (Math.random() < 0.35) {
          this.launchCropRaid();
        }
      }
    }
  }

  private performAIAction(_time: number): void {
    // Random behavior: wander around farm, tend plants
    const action = Math.random();

    if (action < 0.4) {
      // Wander within farm
      this.rivalWander();
    } else if (action < 0.7) {
      // Tend a plant (visual animation)
      this.rivalTendPlant();
    } else {
      // Stand idle
      this.rivalSprite.setVelocity(0, 0);
      const dirs = ['down', 'left', 'right', 'up'];
      this.rivalSprite.play(`player-idle-${dirs[this.rivalDirection]}`, true);
    }
  }

  private rivalWander(): void {
    const targetX = (this.rivalFarmOriginX + Math.random() * 6) * TILE_SIZE;
    const targetY = (this.rivalFarmOriginY + Math.random() * 6) * TILE_SIZE;
    const dx = targetX - this.rivalSprite.x;
    const dy = targetY - this.rivalSprite.y;
    const dist = Math.sqrt(dx * dx + dy * dy);

    if (dist > 5) {
      const speed = 40;
      const vx = (dx / dist) * speed;
      const vy = (dy / dist) * speed;
      this.rivalSprite.setVelocity(vx, vy);

      if (Math.abs(vx) > Math.abs(vy)) {
        this.rivalDirection = vx < 0 ? Direction.LEFT : Direction.RIGHT;
      } else {
        this.rivalDirection = vy < 0 ? Direction.UP : Direction.DOWN;
      }

      const dirs = ['down', 'left', 'right', 'up'];
      this.rivalSprite.play(`player-walk-${dirs[this.rivalDirection]}`, true);
    }

    // Stop after a bit
    this.scene.time.delayedCall(1500, () => {
      this.rivalSprite.setVelocity(0, 0);
    });
  }

  private rivalTendPlant(): void {
    // Find a random plot and do a tending animation
    const plotKeys = Array.from(this.rivalPlots.keys());
    if (plotKeys.length === 0) return;

    const key = plotKeys[Math.floor(Math.random() * plotKeys.length)];
    const plot = this.rivalPlots.get(key);
    if (!plot) return;

    // Water splash effect
    const splash = this.scene.add.circle(
      plot.x * TILE_SIZE + TILE_SIZE / 2,
      plot.y * TILE_SIZE + TILE_SIZE / 2,
      4, 0x6eb5ff, 0.5
    );
    splash.setDepth(20);
    this.scene.tweens.add({
      targets: splash,
      alpha: 0,
      scaleX: 2,
      scaleY: 2,
      duration: 400,
      onComplete: () => splash.destroy(),
    });
  }

  private growRandomRivalPlant(): void {
    for (const [key, plot] of this.rivalPlots) {
      if (plot.plantId && plot.growthStage < 3) {
        plot.growthStage++;
        const species = PLANTS[plot.plantId];
        if (species) {
          const sprite = this.rivalPlantSprites.get(key);
          if (sprite) {
            sprite.setFrame(species.spriteRow * 4 + plot.growthStage);
          }
        }
        break;
      }
    }
  }

  private checkPlayerProximity(): void {
    const playerX = this.scene.player.sprite.x;
    const playerY = this.scene.player.sprite.y;
    const rivalX = this.rivalSprite.x;
    const rivalY = this.rivalSprite.y;

    const dist = Math.sqrt((playerX - rivalX) ** 2 + (playerY - rivalY) ** 2);

    // If player is near rival, show interaction hint
    if (dist < 40) {
      const dayCount = this.scene.dayNightSystem.getDayCount();
      if (dayCount >= this.challengeDay && !this.challengeReady) {
        this.challengeReady = true;
        this.scene.events.emit('notification', 'Thornwick challenges you! Press E near him to battle!');
      }
    }
  }

  /** Called when player interacts near the rival. */
  interactWithRival(): boolean {
    const playerX = this.scene.player.sprite.x;
    const playerY = this.scene.player.sprite.y;
    const dist = Math.sqrt(
      (playerX - this.rivalSprite.x) ** 2 +
      (playerY - this.rivalSprite.y) ** 2
    );

    if (dist > 40) return false;

    // Start a plant battle against the rival
    const playerItem = this.scene.player.getSelectedItem();
    const playerPlantId = playerItem?.plantId ?? 'sunflower';

    // Rival picks their strongest plant
    const rivalPlantId = this.pickRivalBattlePlant();

    this.scene.scene.pause('GameScene');
    this.scene.scene.launch('BattleScene', {
      playerPlantId,
      enemyPlantId: rivalPlantId,
      isNight: this.scene.dayNightSystem.isNight(),
      returnScene: 'GameScene',
    });

    // Listen for battle result to apply stakes
    const onBattleResult = (data: { won: boolean }) => {
      this.scene.events.off('battle-result', onBattleResult);
      if (data.won) {
        // Win: get a seed of the rival's plant species
        this.scene.player.addToInventory(rivalPlantId, 1);
        this.scene.player.unlockPlant(rivalPlantId);
        this.scene.events.emit('notification', `Victory! Won 1x ${PLANTS[rivalPlantId]?.name ?? rivalPlantId} seed!`);
      } else {
        // Lose: pay 5 coins
        const cost = Math.min(5, this.scene.combatSystem.playerCoins);
        this.scene.combatSystem.playerCoins -= cost;
        this.scene.events.emit('combat-stats-update', this.scene.combatSystem.getStats());
        if (cost > 0) {
          this.scene.events.emit('notification', `Defeat... lost ${cost} coins to Thornwick.`);
        }
      }
    };
    this.scene.events.on('battle-result', onBattleResult);

    return true;
  }

  private pickRivalBattlePlant(): string {
    // Progressive difficulty: Thornwick uses stronger plants as days progress
    const dayCount = this.scene.dayNightSystem.getDayCount();
    if (dayCount >= 16) return 'solar_flare';       // Endgame glass cannon
    if (dayCount >= 13) return 'infernal_creeper';   // Hybrid threat
    if (dayCount >= 10) return 'fire_pepper';        // Glass cannon, teaches defense
    return 'moonbloom';                               // Tanky intro fight
  }

  /** Launch a crop raid: spawn a Thorn Crawler that targets the player's farm. */
  private launchCropRaid(): void {
    this.hasRaidedToday = true;
    this.scene.events.emit('notification', 'Thornwick sent a scout to raid your crops!');

    // Spawn a thorn crawler near the player's farm
    if (this.scene.nightMonsterSystem) {
      // Use the existing monster spawning, but note this is during the day
      const farmCenterX = this.scene.player.sprite.x;
      const farmCenterY = this.scene.player.sprite.y;

      const spawnX = farmCenterX + Phaser.Math.Between(-100, 100);
      const spawnY = farmCenterY - Phaser.Math.Between(150, 250);

      // Manually spawn a thorn crawler scout
      const species = MONSTERS['thorn_crawler'];
      if (!species) return;

      const sprite = this.scene.physics.add.sprite(spawnX, spawnY, 'monsters', species.spriteRow * 4);
      sprite.setDepth(9);
      sprite.setSize(12, 12);
      sprite.setOffset(2, 4);
      sprite.setTint(0xdd5533); // Red tint to indicate it's Thornwick's scout

      const hpBar = this.scene.add.graphics();
      hpBar.setDepth(11);

      sprite.setAlpha(0);
      this.scene.tweens.add({
        targets: sprite,
        alpha: 1,
        duration: 500,
        ease: 'Power2',
      });

      // Warning notification
      const warning = this.scene.add.circle(spawnX, spawnY, 12, 0xdd5533, 0.3);
      warning.setDepth(8);
      warning.setStrokeStyle(1, 0xdd5533, 0.6);
      this.scene.tweens.add({
        targets: warning,
        scaleX: 2,
        scaleY: 2,
        alpha: 0,
        duration: 800,
        onComplete: () => warning.destroy(),
      });

      // Add to the monster system's list
      this.scene.nightMonsterSystem.monsters.push({
        id: `raid_scout_${Date.now()}`,
        species,
        sprite,
        hp: species.hp,
        maxHp: species.hp,
        hpBar,
        lastAttackTime: 0,
        dead: false,
        stunned: false,
        stunEndTime: 0,
      });
    }
  }
}
